import { createStyles, withStyles } from '@mui/styles'
import { muiOptions, MuiProps, defaultStyles } from '../../../infrastructure/materialUiThemeProvider'
import React, { useEffect, useState } from 'react'
import { ColumnDescriptor, Select, TableItem, TextField } from '../../common/customComponents'
import { Checkbox, Typography } from '@mui/material'
import { t } from '../../../infrastructure/i18nextHelper'
import * as api from '../../../infrastructure/api'
import guid, { Guid } from '../../../infrastructure/guid'
import { hasClaim } from '../../../infrastructure/signIn/userContext'
import { Claims } from '../../../infrastructure/signIn/models'
import { MasterDataItem, MasterDataShell, createExcelLines } from './masterDataShell'
import { ExcelGeneratorContainer } from '../../../infrastructure/excelExport'
import { SnackbarContainer } from '../../../infrastructure/snackbars'

function RoadSalesProduct({ classes }: MuiProps) {
    let [items, setItems] = useState<RoadSalesProduct[]>([])
    let [cpdpFamilys, setCpdpFamilys] = useState<string[]>([])
    let [marketingFamilys, setMarketingFamilys] = useState<string[]>([])
    let [companys, setCompanys] = useState<Company[]>([])
    let [filters, setFilters] = useState<ItemFilters>(noFilter)
    let [selectedItems, setSelectedItems] = useState<TableItem<MasterDataItem<RoadSalesProduct>>[]>([])

    let isManager = hasClaim(Claims.MasterdataRoadSalesProductManager)
    let tBase = 'admin.masterdata.roadSalesProduct.'
    let excelGenerator = ExcelGeneratorContainer.useContainer()
    let snackbar = SnackbarContainer.useContainer()

    useEffect(() => {
        loadReferentials()
        loadItems()
    }, [])

    let loadReferentials = async () => {
        let cpdpFamilys = await api.get<{ name: string }[]>('masterdata/roadSalesProduct/cpdpFamily')
        setCpdpFamilys(cpdpFamilys.map(x => x.name))

        let marketingFamilys = await api.get<{ name: string }[]>('masterdata/roadSalesProduct/marketingFamily')
        setMarketingFamilys(marketingFamilys.map(x => x.name))

        let companys = await api.get<{ code: string; name: string }[]>('masterdata/roadSalesProduct/company')
        setCompanys(companys)
    }

    let loadItems = async () => {
        let items = await api.get<RoadSalesProduct[]>('masterdata/roadSalesProduct')
        setItems(items)
    }

    let getItems = () => applyFilters(items, filters).map(toTableItem)

    let save = async (item: RoadSalesProduct) => {
        if (item.code === ''
            || item.description === ''
            || item.companyCode === ''
            || item.cpdpFamily === ''
            || item.marketingFamily === ''
            || item.sapShortName === '') {
            return false
        }

        await api.post<RoadSalesProduct>('masterdata/roadSalesProduct', item)
        loadItems()
        return true
    }

    let onDelete = async (ids: string[]) => {
        if (ids.length === 0)
            return false

        await api.del('masterdata/roadSalesProduct', { ids: ids })
        await loadItems()
        return true
    }

    let importExcel = (file: Blob) => {
        let uploadUrl = 'masterdata/roadSalesProduct/import'
        api.upload(uploadUrl, file, 'roadSalesProduct', { withReport: 'dialog' }).then(_ => {
            loadItems()
            snackbar.success(t('httpSuccess.importSuccessful'))
        })
    }

    let exportExcel = () => {
        let lines = selectedItems?.length > 0 ? selectedItems : getItems()
        excelGenerator.generate({
            filename: 'RoadSalesProducts.xlsx',
            sheets: [{ name: 'RoadSalesProducts', lines: createExcelLines(lines, columns) }]
        })
    }

    let columns: ColumnDescriptor<TableItem<MasterDataItem<RoadSalesProduct>>>[] = [
        {
            name: t(tBase + 'company'), value: (item) => item.companyCode,
            columnFilter: {
                value: filters.companyCode ?? '',
                onChange: (value: string) => setFilters({ ...filters, companyCode: value })
            }
        },
        {
            name: t(tBase + 'code'), value: (item) => item.code,
            columnFilter: {
                value: filters.code ?? '',
                onChange: (value: string) => setFilters({ ...filters, code: value })
            }
        },
        {
            name: t(tBase + 'description'), value: (item) => item.description,
            columnFilter: {
                value: filters.description ?? '',
                onChange: (value: string) => setFilters({ ...filters, description: value })
            }
        },
        {
            name: t(tBase + 'cpdpFamily'), value: (item) => item.cpdpFamily,
            columnFilter: {
                value: filters.cpdpFamily ?? '',
                onChange: (value: string) => setFilters({ ...filters, cpdpFamily: value })
            }
        },
        {
            name: t(tBase + 'marketingFamily'), value: (item) => item.marketingFamily,
            columnFilter: {
                value: filters.marketingFamily ?? '',
                onChange: (value: string) => setFilters({ ...filters, marketingFamily: value })
            }
        },
        {
            name: t(tBase + 'withAdditives'),
            value: x => getBooleanValue(x.withAdditives),
            columnFilter: {
                value: filters.withAdditives ?? '',
                onChange: (value) => setFilters({ ...filters, withAdditives: value || null })
            }
        },
        {
            name: t(tBase + 'sapShortName'), value: (item) => item.sapShortName,
            columnFilter: {
                value: filters.sapShortName ?? '',
                onChange: (value: string) => setFilters({ ...filters, sapShortName: value })
            }
        },
        {
            name: t(tBase + 'isActive'),
            value: x => getBooleanValue(x.isActive),
            columnFilter: {
                value: filters.isActive ?? '',
                onChange: (value) => setFilters({ ...filters, isActive: value || null })
            }
        }
    ]

    return (
        <div className={classes.container}>
            <MasterDataShell
                tableId='roadSalesProduct'
                headerLabel={t(tBase + 'roadSalesProducts')}
                itemLabel={t(tBase + 'roadSalesProduct')}
                items={getItems()}
                columns={columns}
                onSave={save}
                onDelete={onDelete}
                onNew={emptyItem}
                onImportExcel={importExcel}
                onExportExcel={exportExcel}
                isManager={hasClaim(Claims.MasterdataPricingPeriodRuleManager)}
                onSelectionChange={(x: TableItem<MasterDataItem<RoadSalesProduct>>[]) => setSelectedItems(x)}
            >{
                    (selectedItem, setSelectedItem) => (
                        <>
                            <Select label={t(tBase + 'company')}
                                disabled={!isManager}
                                value={selectedItem.companyCode}
                                choices={companys.map(c => ({ value: c.code, text: c.name }))}
                                onChange={(value) => setSelectedItem({ ...selectedItem, companyCode: value })}
                            />
                            <TextField label={t(tBase + 'code')}
                                disabled={!isManager}
                                text={selectedItem.code}
                                onChange={(evt) => setSelectedItem({ ...selectedItem, code: evt.target.value })}
                            />
                            <TextField label={t(tBase + 'description')}
                                disabled={!isManager}
                                text={selectedItem.description}
                                onChange={(evt) => setSelectedItem({ ...selectedItem, description: evt.target.value })}
                            />
                            <Select label={t(tBase + 'cpdpFamily')}
                                disabled={!isManager}
                                value={selectedItem.cpdpFamily}
                                choices={cpdpFamilys.map(c => ({ value: c, text: c }))}
                                onChange={(value) => setSelectedItem({ ...selectedItem, cpdpFamily: value })}
                            />
                            <Select label={t(tBase + 'marketingFamily')}
                                disabled={!isManager}
                                value={selectedItem.marketingFamily}
                                choices={marketingFamilys.map(c => ({ value: c, text: c }))}
                                onChange={(value) => setSelectedItem({ ...selectedItem, marketingFamily: value })}
                            />
                            <TextField label={t(tBase + 'sapShortName')}
                                disabled={!isManager}
                                text={selectedItem.sapShortName}
                                onChange={(evt) => setSelectedItem({ ...selectedItem, sapShortName: evt.target.value })}
                            />
                            <div className={classes.booleanContainer}>
                                <Typography variant='subtitle1'>{t(tBase + 'withAdditives')}</Typography>
                                <Checkbox
                                    checked={selectedItem?.withAdditives}
                                    onChange={(e) => setSelectedItem({ ...selectedItem, withAdditives: e.target.checked })} />
                            </div>
                            <div className={classes.booleanContainer}>
                                <Typography variant='subtitle1'>{t(tBase + 'isActive')}</Typography>
                                <Checkbox
                                    checked={selectedItem?.isActive}
                                    onChange={(e) => setSelectedItem({ ...selectedItem, isActive: e.target.checked })} />
                            </div>
                        </>
                    )
                }
            </MasterDataShell>
        </div>
    )
}

type RoadSalesProduct = {
    id: Guid
    code: string
    description: string
    companyCode: string
    cpdpFamily: string
    marketingFamily: string
    withAdditives: boolean
    sapShortName: string
    isActive: boolean
}

let emptyItem = (): RoadSalesProduct => ({
    id: guid.empty,
    code: '',
    description: '',
    companyCode: '',
    cpdpFamily: '',
    marketingFamily: '',
    withAdditives: false,
    sapShortName: '',
    isActive: true
})

let toTableItem = (item: RoadSalesProduct): TableItem<MasterDataItem<RoadSalesProduct>> => ({
    id: item.id,
    code: item.code,
    description: item.description,
    companyCode: item.companyCode,
    cpdpFamily: item.cpdpFamily,
    marketingFamily: item.marketingFamily,
    withAdditives: item.withAdditives,
    sapShortName: item.sapShortName,
    isActive: item.isActive,
    isModified: false
})

type Company = {
    code: string
    name: string
}

type ItemFilters = {
    code: string | null
    description: string | null
    companyCode: string | null
    cpdpFamily: string | null
    marketingFamily: string | null
    withAdditives: string | null
    sapShortName: string | null
    isActive: string | null
}

let noFilter: ItemFilters = {
    code: null,
    description: null,
    companyCode: null,
    cpdpFamily: null,
    marketingFamily: null,
    withAdditives: null,
    sapShortName: null,
    isActive: null
}

let getBooleanValue = (value: boolean) =>
    value ? t('admin.masterdata.roadSalesProduct.isYes') : t('admin.masterdata.roadSalesProduct.isNo')

let applyFilters = (items: RoadSalesProduct[], filters: ItemFilters): RoadSalesProduct[] =>
    items.filter(item => matchesFilter(item.code, filters.code)
        && matchesFilter(item.description, filters.description)
        && matchesFilter(item.companyCode, filters.companyCode)
        && matchesFilter(item.cpdpFamily, filters.cpdpFamily)
        && matchesFilter(item.marketingFamily, filters.marketingFamily)
        && matchesFilter(item.withAdditives, filters.withAdditives)
        && matchesFilter(item.sapShortName, filters.sapShortName)
        && matchesFilter(item.isActive, filters.isActive))

let matchesFilter = (value: string | number | boolean, filter: string | boolean | number | null): boolean => {
    if (filter === null || filter === '')
        return true

    if (typeof value === 'boolean')
        return getBooleanValue(value).toLowerCase().includes(String(filter).toLowerCase())

    return String(value).toLowerCase().includes(String(filter).toLowerCase())
}

let styles = () =>
    createStyles({
        container: { height: '100%' },
        booleanContainer: {
            ...defaultStyles.flexRow,
            width: '15.3em',
            justifyContent: 'space-between',
            "& >span": { padding: '0' }
        }
    })

export default withStyles(styles, muiOptions)(RoadSalesProduct)